import { getUpstreamTasks } from '../../utils/edgePaths/edgeGeneration';
import { isMilestoneNode } from '../../utils/milestoneUtils';
import { isNoteNode } from '../../utils/noteUtils';

const FALLBACK_ORIGIN_X = 100;
const FALLBACK_ORIGIN_Y = 100;
const FALLBACK_COL_WIDTH = 320;
const FALLBACK_ROW_HEIGHT = 180;
const FALLBACK_COLS = 6;

function isFinitePos(pos) {
  return pos && Number.isFinite(pos.x) && Number.isFinite(pos.y);
}

/** Grid slot used until ELK / tidy assigns a real coordinate. Avoids stacking every new card at 100,100. */
export function fallbackLayoutPosition(index) {
  const i = Number.isFinite(index) && index >= 0 ? index : 0;
  const col = i % FALLBACK_COLS;
  const row = Math.floor(i / FALLBACK_COLS);
  return {
    x: FALLBACK_ORIGIN_X + col * FALLBACK_COL_WIDTH,
    y: FALLBACK_ORIGIN_Y + row * FALLBACK_ROW_HEIGHT,
  };
}

export function getSpawnPosition(task, customPositions) {
  const custom = customPositions?.[task.id];
  if (isFinitePos(custom)) return { x: custom.x, y: custom.y };
  if (Number.isFinite(task?.x) && Number.isFinite(task?.y)) {
    return { x: task.x, y: task.y };
  }
  return null;
}

/**
 * Missions with no incoming or outgoing dependency — a right-click "drop here"
 * node, or any other loose card that is not yet wired into a chain.
 */
export function getIsolatedMissionIds(tasks) {
  const isolated = new Set();
  const hasChild = new Set();

  (tasks || []).forEach((task) => {
    if (isMilestoneNode(task) || isNoteNode(task)) return;
    getUpstreamTasks(task, tasks).forEach(({ taskId }) => {
      if (taskId) hasChild.add(taskId);
    });
  });

  (tasks || []).forEach((task) => {
    if (isMilestoneNode(task) || isNoteNode(task)) return;
    if (getUpstreamTasks(task, tasks).length > 0) return;
    if (hasChild.has(task.id)) return;
    isolated.add(task.id);
  });

  return isolated;
}

/** Tasks that should drive implicit ELK — excludes loose drop-here missions. */
export function getConnectedLayoutTasks(tasks) {
  const isolated = getIsolatedMissionIds(tasks);
  if (isolated.size === 0) return tasks || [];
  return (tasks || []).filter((task) => !isolated.has(task.id));
}

/**
 * ELK parks a new disconnected component at the top of the board, and packing
 * a second chain re-origins the existing graph a few pixels up/left. Keep
 * isolated missions at spawn and the already-laid-out graph on its last
 * committed coordinates. Explicit Tidy should not use this.
 */
/**
 * Identity of the connected graph's geometry. Isolated drop-here missions are
 * excluded so adding one does not look like a layout change.
 * Card size or a dependency edit changes the key — those passes must not be
 * pinned back onto the previous coordinates.
 */
export function connectedLayoutPinKey(tasks, nodeDimensions, dependencySignature) {
  const isolated = getIsolatedMissionIds(tasks);
  const dep = (dependencySignature || '')
    .split('|')
    .filter((part) => {
      if (!part) return false;
      const id = part.split(':')[0];
      return !isolated.has(id);
    })
    .sort()
    .join('|');
  const dims = (tasks || [])
    .filter((task) => !isolated.has(task.id))
    .map((task) => {
      const d = nodeDimensions?.[task.id];
      const w = d && Number.isFinite(d.width) ? Math.round(d.width) : 0;
      const h = d && Number.isFinite(d.height) ? Math.round(d.height) : 0;
      return `${task.id}:${w}x${h}`;
    })
    .sort()
    .join('|');
  return `${dep}::${dims}`;
}

/**
 * Hold the previous graph still only when this pass did not change connected
 * card sizes or dependencies (the "new loose card shifted everyone 8px" case).
 * A placeholder-dimension pass, a real remeasure, or a deleted line must be
 * allowed to move nodes — otherwise cards stay packed on top of each other.
 */
export function heldPositionsForImplicitLayout(previousPinKey, nextPinKey, heldPositions) {
  if (!previousPinKey || previousPinKey !== nextPinKey) return null;
  if (!heldPositions || Object.keys(heldPositions).length === 0) return null;
  return heldPositions;
}

export function preserveIsolatedManualPositions(
  elkPositions,
  tasks,
  customPositions,
  heldPositions = null,
) {
  if (!elkPositions) return elkPositions;
  const isolated = getIsolatedMissionIds(tasks);
  if (isolated.size === 0) return elkPositions;

  const next = { ...elkPositions };
  let changed = false;

  isolated.forEach((id) => {
    const task = (tasks || []).find((t) => t.id === id);
    const spawn = getSpawnPosition(task, customPositions);
    if (!spawn) return;
    const elk = next[id];
    if (elk && Math.abs(elk.x - spawn.x) <= 1.5 && Math.abs(elk.y - spawn.y) <= 1.5) return;
    next[id] = spawn;
    changed = true;
  });

  if (heldPositions) {
    (tasks || []).forEach((task) => {
      if (isolated.has(task.id)) return;
      const held = heldPositions[task.id];
      if (!isFinitePos(held)) return;
      const elk = next[task.id];
      if (elk && Math.abs(elk.x - held.x) <= 1.5 && Math.abs(elk.y - held.y) <= 1.5) return;
      next[task.id] = { x: held.x, y: held.y };
      changed = true;
    });
  }

  return changed ? next : elkPositions;
}

export function mergeIsolatedSpawnPositions(elkPositions, tasks, customPositions) {
  const isolated = getIsolatedMissionIds(tasks);
  if (isolated.size === 0) return elkPositions || {};
  const next = { ...(elkPositions || {}) };
  let slot = 0;
  isolated.forEach((id) => {
    const task = (tasks || []).find((t) => t.id === id);
    const spawn = getSpawnPosition(task, customPositions);
    if (spawn) {
      next[id] = spawn;
    } else if (!isFinitePos(next[id])) {
      next[id] = fallbackLayoutPosition(slot);
      slot += 1;
    }
  });
  return next;
}
